import { useNavigate } from '@tanstack/react-router'

import { useUserAccountContextMutation } from '@/api/v1/auth/get-user-account-context'
import { useUserAccountContextsMutation } from '@/api/v1/auth/get-user-account-contexts'
import { CreateAccountDialog } from '@/components/common/create-account-dialog'
import { logger } from '@/lib/logger'
import { useContextStore } from '@/stores/context'
import { useManagedAccountStore } from '@/stores/managed-account'
import { useSandboxStore } from '@/stores/sandbox'

interface CreateAccountFlowProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Account creation, from the dialog to landing on the new account's plans.
 * Lives outside <Sidebar> because the entry point is a sidebar menu item: on
 * mobile that sidebar is a sheet which closes — and unmounts — on the click
 * that opens this.
 */
export function CreateAccountFlow({
  open,
  onOpenChange,
}: CreateAccountFlowProps) {
  const navigate = useNavigate()
  const getUserContext = useUserAccountContextMutation()
  const getUserAccounts = useUserAccountContextsMutation()
  const setSelectedUserAccountId = useContextStore(
    (state) => state.setSelectedUserAccountId,
  )
  const setAccountContext = useContextStore(
    (state) => state.setUserAccountContext,
  )
  const clearSandbox = useSandboxStore((state) => state.clearSandbox)
  const clearManagedAccount = useManagedAccountStore(
    (state) => state.clearManagedAccount,
  )

  const handleComplete = async (userAccountId: string) => {
    onOpenChange(false)

    try {
      setSelectedUserAccountId(userAccountId)
      clearSandbox()
      clearManagedAccount()

      const response = await getUserContext.mutateAsync()
      setAccountContext(response)

      const accountsData = await getUserAccounts.mutateAsync()
      useContextStore.getState().setAvailableUserAccounts(accountsData.items)

      await navigate({
        to: '/settings/billing/plans',
      })
      window.location.reload()
    } catch (error) {
      logger.error(
        'Error switching to new account',
        error instanceof Error ? error : undefined,
      )
    }
  }

  return (
    <CreateAccountDialog
      open={open}
      onOpenChange={onOpenChange}
      onComplete={handleComplete}
    />
  )
}
